"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ShieldCheck, UserCheck, Calendar, ArrowRight, Clock, Sparkles, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const trustPoints = [{
  id: "1",
  title: "1-on-1 Direct Access",
  desc: "You work directly with me—Yassine Essaadi. No account managers or junior pass-offs."
}, {
  id: "2",
  title: "Tailored Growth Strategy",
  desc: "Actionable technical & content insights built specifically for your niche and competition."
}, {
  id: "3",
  title: "Zero Sales Pressure",
  desc: "An honest, clear evaluation of your organic search potential with practical next steps."
}];
function ConsultationFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-muted/50 rounded-xl border border-border">
        <div className="size-12 rounded-full bg-accent/20 flex items-center justify-center text-accent">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-bold font-default text-foreground">
          Consultation Request Received
        </h3>
        <p className="text-muted-foreground text-sm max-w-md">
          {successMessage || "Thank you! I will personally review your site and get back to you within 24 business hours."}
        </p>
        <Button type="button" variant="outline" onClick={resetForm} className="mt-2">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="fullName" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background border-input text-foreground px-3 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium text-sm">Business Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="sarah@company.com" className="bg-background border-input text-foreground px-3 py-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="websiteUrl" rules={{
        required: "Website URL is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium text-sm">Website URL</FormLabel>
              <FormControl>
                <Input placeholder="https://yourcompany.com" className="bg-background border-input text-foreground px-3 py-2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="primaryGoal" rules={{
      required: "Please share a short note on your goal"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">Primary SEO Goal or Challenge</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Higher UK rankings, AI search visibility, or technical audit" className="bg-background border-input text-foreground px-3 py-2" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-11 px-6 rounded-md shadow-md transition-[color,background-color,box-shadow]">
          {isSubmitting ? <>
              <Loader2 className="animate-spin size-4 mr-2" />
              Processing Request...
            </> : <>
              <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
              Book My Free SEO Strategy Session
              <ArrowRight className="size-4 ml-1" />
            </>}
        </Button>
      </div>

      <p className="text-xs text-center text-muted-foreground pt-1">
        No obligation or rigid lock-in. Your information is 100% confidential.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-t border-border relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {/* Left Column: Heading & Content */}
          <motion.div className="lg:col-span-7 space-y-6" variants={itemVariants}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-primary text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="size-3.5 text-accent" />
              Direct Expert Consultation
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default text-foreground tracking-tight leading-tight">
              Book a Free SEO Strategy Consultation with Me
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              When you schedule a consultation with YE Consultancy, you speak directly with me—<strong className="text-foreground font-semibold">Yassine Essaadi</strong>. I do not outsource work to junior staff or rely on generic automated audit tools.
            </p>

            <p className="text-base text-muted-foreground leading-relaxed">
              Together, we will review your website&apos;s technical health, keyword positioning, and organic potential to create a realistic, high-impact growth strategy.
            </p>

            {/* Trust highlights */}
            <div className="space-y-4 pt-2">
              {trustPoints.map((point, index) => <div key={point.id} data-index={index} className="flex items-start gap-3.5">
                  <div className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold font-default text-foreground">
                      {point.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {point.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Guarantees */}
            <div className="pt-4 flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-t border-border">
              <div className="flex items-center gap-2">
                <UserCheck className="size-4 text-primary" />
                <span>Sole Consultant Lead</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-primary" />
                <span>24h Response Time</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                <span>Strict NDA Protection</span>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                Prefer to write directly? Reach me at{" "}
                <a href="mailto:contact@ye-consultancy.com" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                  contact@ye-consultancy.com
                </a>{" "}
                or view my detailed{" "}
                <Link to="/ContactPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                  Contact Page
                </Link>.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Interactive Consultation Booking Form */}
          <motion.div className="lg:col-span-5" variants={itemVariants}>
            <Card className="bg-card text-card-foreground border border-border shadow-xl rounded-2xl overflow-hidden">
              <div className="bg-primary/5 px-6 py-4 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="font-semibold font-default text-foreground text-base">
                    Request Strategy Session
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    100% Free • No obligation pitch
                  </p>
                </div>
                <div className="size-8 rounded-full bg-accent/15 text-accent flex items-center justify-center">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                </div>
              </div>

              <CardContent className="p-6">
                <FormProvider formId="consultation-cta-form" sectionName="ConsultationCTA" defaultValues={{
                fullName: "",
                email: "",
                websiteUrl: "",
                primaryGoal: ""
              }} validationRules={{
                fullName: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                websiteUrl: {
                  required: true
                },
                primaryGoal: {
                  required: true
                }
              }} submitText="Book My Free SEO Strategy Session" successMessage="Thank you! Yassine will personally review your website and reply within 24 business hours.">
                  <ConsultationFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}